// import parseTime, formatTime and set to filter
import dayjs from 'dayjs';
import { forceDecimal, numberFormat } from '@zk/framework-vue';
import i18n, { getLanguage } from 'common-module/src/lang';

/**
 * Show plural label if time is plural number
 * @param {number} time
 * @param {string} label
 * @return {string}
 */
function pluralize(time, label) {
  if (time === 1) {
    return time + label;
  }
  return time + label + 's';
}

/**
 * @param {number} time
 */
export function timeAgo(time) {
  const between = Date.now() / 1000 - Number(time);
  if (between < 3600) {
    return pluralize(~~(between / 60), ' minute');
  } if (between < 86400) {
    return pluralize(~~(between / 3600), ' hour');
  }
  return pluralize(~~(between / 86400), ' day');

}

/**
 * Number formatting
 * like 10000 => 10k
 * @param {number} num
 * @param {number} digits
 */
export function numberFormatter(num, digits) {
  const si = [
    { value: 1e18, symbol: 'E' },
    { value: 1e15, symbol: 'P' },
    { value: 1e12, symbol: 'T' },
    { value: 1e9, symbol: 'G' },
    { value: 1e6, symbol: 'M' },
    { value: 1e3, symbol: 'k' }
  ];
  for (let i = 0; i < si.length; i++) {
    if (num >= si[i].value) {
      return (num / si[i].value + 0.1).toFixed(digits).replace(/\.0+$|(\.[0-9]*[1-9])0+$/, '$1') + si[i].symbol;
    }
  }
  return num.toString();
}

/**
 * 毫秒转日期
 * @param {Number} time 毫秒数
 * @param {String} format 转换后的格式
 */
export function date(time, format) {
  if (!time) {
    return;
  }
  const t = new Date(time);
  const tf = function (i) {
    return (i < 10 ? '0' : '') + i;
  };
  const monthEn = function (i) {
    return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'June', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'][i];
  };
  const tfHour = function (hours) {
    return hours > 12 ? hours - 12 : hours;
  };
  const tApm = function (hours) {
    return hours > 12 ? 'PM' : 'AM';
  };
  return format.replace(/yyyy|MMM|MM|dd|hh|HH|mm|ss|APM|apm/g, function (a) {
    switch (a) {
      case 'yyyy':
        return tf(t.getFullYear());
      case 'MM':
        return tf(t.getMonth() + 1);
      case 'MMM':
        return monthEn(t.getMonth());
      case 'mm':
        return tf(t.getMinutes());
      case 'dd':
        return tf(t.getDate());
      case 'HH':
        return tf(t.getHours());
      case 'hh':
        return tfHour(t.getHours());
      case 'ss':
        return tf(t.getSeconds());
      case 'APM':
        return tf(tApm(t.getHours()));
      case 'apm':
        return tf(tApm(t.getHours())).toLocaleLowerCase();
    }
  });
}

/**
 * 10000 => "10,000"
 * @param {number} num
 */
export function toThousandFilter(num) {
  return (+num || 0).toString().replace(/^-?\d+/g, m => m.replace(/(?=(?!\b)(\d{3})+$)/g, ','));
}

/**
 * Upper case first char
 * @param {String} string
 */
export function uppercaseFirst(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

/**
 * 格式化日期 默认: YYYY-MM-DD
 */
export const formatDate = (date, format) => {
  return dayjs(date).isValid() ? dayjs(date).format(format || i18n.dateFormat.date) : '';
};

// 格式化日期时间 默认: YYYY-MM-DD HH:mm
export const formatDateTime = (date, { showSecond = false, format } = {}) => {
  const defaultFormat = showSecond ? i18n.dateFormat.dateTimeHasSeconds : i18n.dateFormat.dateTime;
  return formatDate(date, (format || defaultFormat));
};

// 格式化日期时间 默认显示秒: YYYY-MM-DD HH:mm:ss
export const formatDateTimeSecond = (date) => formatDateTime(date, { showSecond: true });


/**
 * 日历格式化
 * @param date
 * @param formats Object @see https://dayjs.fenxianglu.cn/category/plugin.html#%E6%97%A5%E5%8E%86
 * [yesterday] [today] [tomorrow]
 * @returns {string|string}
 */
export const formatCalendarDate = (date, formats) => {
  const formatZh = { sameDay: '[today]', nextDay: '[tomorrow]', lastDay: '[yesterday]', nextWeek: 'MM-DD', lastWeek: 'MM-DD', sameYear: 'MM-DD', sameElse: 'YYYY-MM-DD', ...formats };
  const formatEn = { sameDay: '[today]', nextDay: '[tomorrow]', lastDay: '[yesterday]', nextWeek: 'MMM D', lastWeek: 'MMM D', sameYear: 'MMM D', sameElse: 'MMM D, YYYY', ...formats };
  return dayjs(date).isValid() ? dayjs(date).calendar(null, (getLanguage() === 'zh' ? formatZh : formatEn)) : '';
};

/**
 * 格式化日历日期时间：时分秒
 * @param date
 * @param showSecond 是否显示秒,默认false
 * @param formats Object
 * ([yesterday] | [today] | [tomorrow]) HH:mm
*/
export const formatCalendarDateTime = (date, { showSecond = false, formats } = {}) => {
  const timeFormat = showSecond ? 'HH:mm:ss' : 'HH:mm';
  const formatZh = { sameDay: `[today] ${timeFormat}`, nextDay: `[tomorrow] ${timeFormat}`, lastDay: `[yesterday] ${timeFormat}`, nextWeek: `MM-DD ${timeFormat}`, lastWeek: `MM-DD ${timeFormat}`, sameYear: `MM-DD ${timeFormat}`, sameElse: `YYYY-MM-DD ${timeFormat}`, ...formats };
  const formatEn = { sameDay: `[today] ${timeFormat}`, nextDay: `[tomorrow] ${timeFormat}`, lastDay: `[yesterday] ${timeFormat}`, nextWeek: `MMM D ${timeFormat}`, lastWeek: `MMM D ${timeFormat}`, sameYear: `MMM D ${timeFormat}`, sameElse: `MMM D, YYYY ${timeFormat}`, ...formats };
  return formatCalendarDate(date, (getLanguage() === 'zh' ? formatZh : formatEn));
};

/**
 * 格式化日期时间，继承formatCalendarDateTime
 * "今天"不显示语义化，只显示时间
 */
export const formatCalendarDateTimeNoToday = (date) => formatCalendarDateTime(date, { showSecond: false, formats: { sameDay: 'HH:mm' } });

// 格式化日历日期时间，默认显示秒 ([yesterday] | [today] | [tomorrow]) HH:mm:ss
export const formatCalendarDateTimeSecond = (date) => formatCalendarDateTime(date, { showSecond: true });


export const replaceURLWithHTMLLinks = (text) => text.replace(/(\b(https?|ftp|file|localhost):\/\/[-A-Z0-9+&@#/%?=()~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/ig, '<a style="color: var(--primary-color)" onclick="(arguments[0] || window.event).stopPropagation();" target="_blank" href="$1">$1</a>');

export const amount = (number, { decimal = 2, unit = '¥', sp = ',', spCount = 3 } = {}) => {
  const value = forceDecimal(number, decimal, true);
  const [num, dec = ''] = value.split('.');
  return `${unit} ${numberFormat(num, spCount, sp)}${dec ? '.' : ''}${dec}`;
};
